import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  listWorkspaces,
  createWorkspace,
  duplicateWorkspace,
  archiveWorkspace,
} from '../../../api/workspaceApi';
import { useAuth } from '../../../contexts/AuthContext';
import { ROUTES } from '../../../utils/constants';

export default function StudioSidebar({ selectedWorkspaceId, onImportLauncher }) {
  const { tenantId } = useAuth();
  const navigate     = useNavigate();

  const [workspaces,    setWorkspaces]    = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [isCreating,    setIsCreating]    = useState(false);
  const [newName,       setNewName]       = useState('');
  const [showCreate,    setShowCreate]    = useState(false);
  const [menuAnchor,    setMenuAnchor]    = useState(null);
  const [menuTarget,    setMenuTarget]    = useState(null);

  const loadWorkspaces = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    try {
      const resp  = await listWorkspaces();
      const items = resp?.data?.items ?? resp?.items ?? [];
      setWorkspaces(items);
    } catch (err) {
      console.warn('[StudioSidebar] Failed to load workspaces', err?.message);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      const resp = await createWorkspace({ name: newName.trim() });
      const ws   = resp?.data ?? resp;
      setNewName('');
      setShowCreate(false);
      await loadWorkspaces();
      if (ws?.id) navigate(ROUTES.STUDIO_WORKSPACE.replace(':workspaceId', ws.id));
    } catch (err) {
      console.warn('[StudioSidebar] Failed to create workspace', err?.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDuplicate = async (wsId) => {
    setMenuAnchor(null);
    try {
      await duplicateWorkspace(wsId);
      await loadWorkspaces();
    } catch (err) {
      console.warn('[StudioSidebar] Failed to duplicate workspace', err?.message);
    }
  };

  const handleArchive = async (wsId) => {
    setMenuAnchor(null);
    try {
      await archiveWorkspace(wsId);
      await loadWorkspaces();
      if (wsId === selectedWorkspaceId) navigate(ROUTES.STUDIO);
    } catch (err) {
      console.warn('[StudioSidebar] Failed to archive workspace', err?.message);
    }
  };

  return (
    <Box
      sx={{
        width:           240,
        flexShrink:      0,
        borderRight:     '1px solid',
        borderColor:     'divider',
        display:         'flex',
        flexDirection:   'column',
        height:          '100%',
        overflow:        'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Layouts
        </Typography>
        <Tooltip title="New layout">
          <IconButton size="small" onClick={() => setShowCreate((v) => !v)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {showCreate && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            placeholder="Layout name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
              fullWidth
            >
              {isCreating ? <CircularProgress size={14} /> : 'Create'}
            </Button>
            <Button size="small" onClick={() => setShowCreate(false)} fullWidth>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Divider />

      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {workspaces.map((ws) => (
              <ListItem
                key={ws.id}
                disablePadding
                secondaryAction={
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(ws.id); }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={ws.id === selectedWorkspaceId}
                  onClick={() => navigate(ROUTES.STUDIO_WORKSPACE.replace(':workspaceId', ws.id))}
                  sx={{ pr: 5 }}
                >
                  <ListItemText
                    primary={ws.name}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                    secondary={
                      ws.publishedVersionId
                        ? <Chip label="Published" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                        : <Chip label="Draft only" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}

            {!workspaces.length && !isLoading && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No layouts yet. Click + to create one.
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>

      {/* Import from Launcher */}
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={onImportLauncher}
        >
          Import Launcher
        </Button>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => { setMenuAnchor(null); navigate(`/admin/workspaces/${menuTarget}`); }}
        >
          <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
          Manage / Assignments
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(menuTarget)}>Duplicate</MenuItem>
        <MenuItem onClick={() => handleArchive(menuTarget)} sx={{ color: 'error.main' }}>
          Archive
        </MenuItem>
      </Menu>
    </Box>
  );
}
